import './styles/themes.css';
import './styles/main.css';
import { buyGather, buyNestBox, buyRooster, buyUpgrade, crow, placeHen } from './core/actions';
import { earn } from './core/economy';
import { spawnEvent } from './core/events';
import type { GoalFocus } from './core/goals';
import { loadSprites } from './render/sprites';
import { clickCrow, setSound, soundOn } from './platform/audio';
import { initTelemetry, track } from './platform/telemetry';
import { game } from './ui/controller';
import { handleMessage, showIntro, showWelcomeBack } from './ui/dialogs';
import { toggleDevPanel } from './ui/dev';
import { byId } from './ui/dom';
import { initFarmView, renderFarm } from './ui/farmView';
import { initHud, updateHud } from './ui/hud';
import { modalOpen } from './ui/modals';
import { farmPanel } from './ui/panels/farm';
import { goalsPanel } from './ui/panels/goals';
import { helpPanel } from './ui/panels/help';
import { hensPanel, placeInto } from './ui/panels/hens';
import { roostersPanel } from './ui/panels/roosters';
import { applyTheme, settingsPanel } from './ui/panels/settings';
import { statsPanel } from './ui/panels/stats';
import { upgradesPanel } from './ui/panels/upgrades';
import { refreshShop, registerPanels, setTab, type TabId } from './ui/shop';
import { toast } from './ui/toasts';

const FOCUS_TAB: Record<GoalFocus, TabId | null> = { nests: null, hens: 'hens', farm: 'farm', roosters: 'roosters', upgrades: 'upgrades', goals: 'goals' };

function updateSoundButton(): void {
  byId('sound-btn').textContent = soundOn() ? '🔊' : '🔇';
}

async function main(): Promise<void> {
  applyTheme(game.prefs.theme);
  await loadSprites();

  initHud((focus) => { const tab = FOCUS_TAB[focus]; if (tab) setTab(tab, true); });
  initFarmView((boxIndex) => { placeInto(boxIndex); setTab('hens', true); });
  registerPanels([hensPanel, upgradesPanel, farmPanel, roostersPanel, goalsPanel, statsPanel, helpPanel, settingsPanel]);
  game.handlers(handleMessage, showWelcomeBack);
  initTelemetry(game.prefs.telemetry);
  track({ type: 'session_start', fresh: game.fresh, version: '1.0.0' });
  if (game.loadError) toast(`Your save couldn't be read (${game.loadError}). It was kept aside and a new farm was started.`, 'warn', 'Save problem', 12000);

  // Sound needs a user gesture; restore the preference on the first click.
  if (game.prefs.sound) window.addEventListener('pointerdown', () => { setSound(true); updateSoundButton(); refreshShop(true); }, { once: true });
  byId('sound-btn').addEventListener('click', () => {
    setSound(!soundOn());
    game.setPrefs({ sound: soundOn() });
    updateSoundButton();
    refreshShop(true);
  });
  byId('help-btn').addEventListener('click', () => setTab('help', true));

  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) { e.preventDefault(); toggleDevPanel(); return; }
    const t = e.target as HTMLElement;
    if (e.code === 'Space' && !modalOpen() && !/INPUT|TEXTAREA|SELECT|BUTTON/.test(t.tagName)) {
      e.preventDefault();
      const f = game.viewFlock();
      if (f.rooster && crow(game.state, game.ctx(), f.id).ok) clickCrow(f.rooster.breed);
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) game.save();
    else game.resume();
  });
  window.addEventListener('pagehide', () => game.save());

  game.start();
  if (game.fresh && !game.prefs.introDone) showIntro();
  if (new URLSearchParams(location.search).has('dev')) toggleDevPanel();

  let lastUi = 0;
  const loop = (now: number) => {
    game.frame(now);
    renderFarm(now);
    if (now - lastUi >= 250) {
      lastUi = now;
      updateHud();
      refreshShop();
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  // Test hook for Playwright (tests/e2e). Harmless in production.
  (window as unknown as { __HH__: unknown }).__HH__ = { game, earn, spawnEvent, placeHen, buyNestBox, buyRooster, buyGather, buyUpgrade };
}

void main();
